import React, { useState, useEffect } from 'react';
import { Plus, Users, TrendingUp, Pencil, Activity, BarChart2, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useFetchUtilizationAnalytics } from '../../hooks/Analytic/useFetchUtilizationAnalytics';
import { LeadsTable } from '../practice/LeadsTable';

interface ClinicianManagementProps {
  groupId?: string;
}

type SortKey = 'fullName' | 'monthUtil' | 'weekUtil' | 'monthBooked';
type SortDir = 'asc' | 'desc';

function UtilBar({ pct }: { pct: number }) {
  const color =
    pct >= 90 ? 'bg-emerald-500' :
    pct >= 70 ? 'bg-blue-500' :
    pct >= 40 ? 'bg-amber-400' :
    'bg-rose-400';
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
      <div
        className={`h-2 rounded-full transition-all duration-500 ${color}`}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
  );
}

function PctBadge({ pct }: { pct: number }) {
  const { bg, text } =
    pct >= 90 ? { bg: 'bg-emerald-100', text: 'text-emerald-700' } :
    pct >= 70 ? { bg: 'bg-blue-100', text: 'text-blue-700' } :
    pct >= 40 ? { bg: 'bg-amber-100', text: 'text-amber-700' } :
    { bg: 'bg-rose-100', text: 'text-rose-600' };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${bg} ${text}`}>
      {pct.toFixed(1)}%
    </span>
  );
}

export const ClinicianManagement: React.FC<ClinicianManagementProps> = () => {
  const [sortKey, setSortKey] = useState<SortKey>('monthUtil');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const practiceId: string | undefined = JSON.parse(localStorage.getItem('user') || '{}')?.[`custom:id_practice`];

  const { clinicianUtil, isLoading: isAnalyticLoading, error: analyticError, fetchAnalytics } = useFetchUtilizationAnalytics();

  useEffect(() => {
    if (practiceId) {
      fetchAnalytics(practiceId);
    }
  }, [practiceId, fetchAnalytics]);

  // No practice configured yet
  if (!practiceId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-100/40 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users className="h-8 w-8 text-purple-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No practice found</h2>
          <p className="text-gray-500 text-sm">Your account isn't linked to a practice yet. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  // Sorting
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ k }: { k: SortKey }) =>
    sortKey !== k ? null :
    sortDir === 'asc' ? <ChevronUp className="inline h-3 w-3 ml-0.5" /> : <ChevronDown className="inline h-3 w-3 ml-0.5" />;

  const Th = ({ label, k }: { label: string; k: SortKey }) => (
    <th
      onClick={() => handleSort(k)}
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-800 transition-colors"
    >
      {label}<SortIcon k={k} />
    </th>
  );

  if (isAnalyticLoading || !clinicianUtil) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-100/40 p-6 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-600">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
          <span className="text-lg">Loading analytics…</span>
        </div>
      </div>
    );
  }

  const { meta, data: clinicians } = clinicianUtil;

  const sorted = [...clinicians].sort((a, b) => {
    let av = 0, bv = 0;
    if (sortKey === 'fullName') return sortDir === 'asc'
      ? a.fullName.localeCompare(b.fullName)
      : b.fullName.localeCompare(a.fullName);
    if (sortKey === 'monthUtil') { av = a.month.utilizationPct; bv = b.month.utilizationPct; }
    if (sortKey === 'weekUtil')  { av = a.week.utilizationPct;  bv = b.week.utilizationPct;  }
    if (sortKey === 'monthBooked') { av = a.month.bookedSlots; bv = b.month.bookedSlots; }
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  if (analyticError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-100/40 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Activity className="h-8 w-8 text-rose-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load analytics</h2>
          <p className="text-gray-500 text-sm mb-4">{analyticError}</p>
          <button
            onClick={() => fetchAnalytics(practiceId!)}
            className="px-4 py-2 bg-purple-600 text-white text-sm rounded-xl hover:bg-purple-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-100/40 p-6 space-y-6">

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Practice Overview
          </h1>
          <p className="text-gray-500 mt-1">Clinician utilization and capacity at a glance</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Month Utilization',
            value: `${(meta.practiceMonthUtilizationPct ?? 0).toFixed(1)}%`,
            sub: meta.monthRange,
            icon: BarChart2,
            color: 'from-purple-500 to-indigo-600',
            bg: 'from-purple-50 to-indigo-50',
          },
          {
            label: 'Week Utilization',
            value: `${(meta.practiceWeekUtilizationPct ?? 0).toFixed(1)}%`,
            sub: meta.weekRange,
            icon: Activity,
            color: 'from-blue-500 to-cyan-600',
            bg: 'from-blue-50 to-cyan-50',
          },
          {
            label: 'Total Clinicians',
            value: meta.clinicianCount,
            sub: 'in this practice',
            icon: Users,
            color: 'from-emerald-500 to-teal-600',
            bg: 'from-emerald-50 to-teal-50',
          },
          {
            label: 'Total Monthly Sessions',
            value: clinicians.reduce((s, c) => s + c.month.totalSlots, 0),
            sub: `${clinicians.reduce((s, c) => s + c.month.bookedSlots, 0)} booked`,
            icon: TrendingUp,
            color: 'from-amber-500 to-orange-500',
            bg: 'from-amber-50 to-orange-50',
          },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="group relative p-5 bg-white/70 backdrop-blur-xl rounded-2xl border border-white/20 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] overflow-hidden">
              <div className={`absolute inset-0 bg-gradient-to-br ${s.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl`} />
              <div className="relative z-10 flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{s.label}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{s.value}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>
                </div>
                <div className={`p-3 bg-gradient-to-r ${s.color} rounded-xl shadow-md`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Utilization Table */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/20 shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100/60 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Clinician Utilization</h2>
            <p className="text-xs text-gray-400 mt-0.5">Week: {meta.weekRange} · Month: {meta.monthRange}</p>
          </div>
          <span className="text-xs text-gray-400">{sorted.length} clinicians</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/60 border-b border-gray-100">
              <tr>
                <Th label="Clinician" k="fullName" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Title</th>
                <Th label="Month %" k="monthUtil" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Month Sessions</th>
                <Th label="Week %" k="weekUtil" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Week Sessions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400">No utilization data available</td>
                </tr>
              ) : sorted.map((c, i) => (
                <tr key={c.clinicianId ?? i} className="hover:bg-blue-50/30 transition-colors duration-150">
                  {/* Clinician */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {c.fullName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <span className="font-medium text-gray-900 whitespace-nowrap">{c.fullName}</span>
                    </div>
                  </td>
                  {/* Title */}
                  <td className="px-4 py-3">
                    <span className="inline-block bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full">{c.title}</span>
                  </td>
                  {/* Month % */}
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1 min-w-[100px]">
                      <PctBadge pct={c.month.utilizationPct} />
                      <UtilBar pct={c.month.utilizationPct} />
                    </div>
                  </td>
                  {/* Month Sessions */}
                  <td className="px-4 py-3 text-gray-700">
                    <span className="font-semibold">{c.month.bookedSlots}</span>
                    <span className="text-gray-400"> / {c.month.totalSlots}</span>
                    {c.month.openSlots > 0 && (
                      <span className="ml-1.5 text-xs text-amber-600">({c.month.openSlots} open)</span>
                    )}
                  </td>
                  {/* Week % */}
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1 min-w-[100px]">
                      <PctBadge pct={c.week.utilizationPct} />
                      <UtilBar pct={c.week.utilizationPct} />
                    </div>
                  </td>
                  {/* Week Sessions */}
                  <td className="px-4 py-3 text-gray-700">
                    <span className="font-semibold">{c.week.bookedSlots}</span>
                    <span className="text-gray-400"> / {c.week.totalSlots}</span>
                    {c.week.openSlots > 0 && (
                      <span className="ml-1.5 text-xs text-amber-600">({c.week.openSlots} open)</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white/60 backdrop-blur-xl rounded-2xl border border-white/20 shadow-md p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Link to="/practice/advertise" className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-100/50 hover:from-purple-100 hover:to-indigo-100 transition-all duration-200">
            <div className="p-2 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl">
              <Plus className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-medium text-gray-900 text-sm">Manage Advertisements</div>
              <div className="text-xs text-gray-500">Manage your advertisements</div>
            </div>
          </Link>
          <Link to="/practice/clinicians" className="flex items-center gap-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100/50 hover:from-emerald-100 hover:to-teal-100 transition-all duration-200">
            <div className="p-2 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl">
              <Users className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-medium text-gray-900 text-sm">Manage Clinicians</div>
              <div className="text-xs text-gray-500">View and edit clinician profiles</div>
            </div>
          </Link>
          <Link to="/practice/blog" className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100/50 hover:from-blue-100 hover:to-cyan-100 transition-all duration-200">
            <div className="p-2 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl">
              <Pencil className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-medium text-gray-900 text-sm">Add Blog Posts</div>
              <div className="text-xs text-gray-500">Earn $30 credit when published</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Leads Table */}
      <LeadsTable idPractice={practiceId ?? ''} />
    </div>
  );
};